// IAMMUZIK legal/policy content — plain language, ready for a lawyer to review.
// Contact: support@iam-muzik.com (domain: iam-muzik.com).

export const CONTACT_EMAIL = 'support@iam-muzik.com';
export const COPYRIGHT_EMAIL = 'support@iam-muzik.com';
export const BUSINESS_NAME = 'IAMMUZIK';
export const LAST_UPDATED = 'August 26, 2026';

export type PolicySection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type Policy = {
  slug: string;
  title: string;
  updated: string;
  intro?: string;
  sections: PolicySection[];
};

export const POLICIES: Record<string, Policy> = {
  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    updated: LAST_UPDATED,
    intro:
      'These Terms govern your use of IAMMUZIK. By creating an account or using the site, you agree to them. If you do not agree, do not use the platform.',
    sections: [
      {
        heading: '1. Eligibility',
        paragraphs: [
          'You must be at least 13 years old, or older where required by your local law. If you are under 18, you confirm that a parent or guardian has reviewed these Terms and agreed on your behalf.',
        ],
      },
      {
        heading: '2. Your account',
        bullets: [
          'You are responsible for keeping your password secure and for all activity on your account.',
          'Provide accurate information when you register.',
          'Each person may have only one account, and accounts are non-transferable.',
          'IAMMUZIK may suspend or close accounts that break these Terms.',
        ],
      },
      {
        heading: '3. Subscriptions',
        paragraphs: [
          'Paid Contracts (tiers) are recurring subscriptions. You authorize IAMMUZIK (through our payment processor, Stripe) to charge your card on a recurring basis until you cancel.',
          '“Promotion” on IAMMUZIK means IAMMUZIK commits to certain actions — such as featuring a track or badge placement. It never guarantees streams, followers, sales, playlist placement, or career outcomes.',
        ],
        bullets: [
          'Prices and billing intervals are shown on the Contracts page before you subscribe.',
          'You can cancel at any time. Cancellation stops future charges but does not refund the current paid period.',
          'Access tied to a tier ends when the subscription ends or is downgraded.',
        ],
      },
      {
        heading: '4. Battles',
        paragraphs: [
          'Battles are skill-based competitions judged by scored judges and audience vote. Prizes are badges, promotion, and timed tier upgrades — never cash.',
        ],
        bullets: [
          'Entering a battle gives IAMMUZIK permission to host and display your track as part of that battle.',
          'IAMMUZIK does not guarantee any battle outcome or promotion result.',
        ],
      },
      {
        heading: '5. Content moderation',
        paragraphs: [
          'IAMMUZIK can review, restrict, remove, or decline to publish any content, and can suspend accounts for violations. Public uploads are moderated before appearing on The Wave.',
        ],
      },
      {
        heading: '6. Intellectual property',
        bullets: [
          'You keep ownership of your original music and lyrics unless you sign a separate, clearly disclosed contract that says otherwise.',
          'You must only upload material you own or have permission to use.',
          'IAMMUZIK receives a limited license to host, display, and stream the content you upload as needed to run the service.',
        ],
      },
      {
        heading: '7. Limits on liability',
        paragraphs: [
          'IAMMUZIK is provided “as is.” To the fullest extent allowed by law, IAMMUZIK is not liable for indirect, incidental, or consequential damages, or for lost profits or data. Our total liability for any claim is limited to the amount you paid us in the prior 12 months.',
        ],
      },
      {
        heading: '8. Changes',
        paragraphs: [
          'We may update these Terms. Material changes will be noted with the updated date above. Continued use after changes means you accept them.',
        ],
      },
      {
        heading: '9. Contact',
        paragraphs: [`Questions about these Terms? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },

  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    updated: LAST_UPDATED,
    intro:
      'This policy explains what personal information IAMMUZIK collects, why, where it is stored, and how to reach us.',
    sections: [
      {
        heading: '1. What we collect',
        bullets: [
          'Account information: username, email address, and password (stored as a salted hash, never in plain text).',
          'Content you upload: audio files, lyrics you type, artwork, and song metadata.',
          'Usage information: pages visited, play counts, and similar activity logs.',
          'Payment information is handled by Stripe. We never see or store your full card number.',
        ],
      },
      {
        heading: '2. Why we collect it',
        bullets: [
          'To run the service: authentication, storage, streaming, battles, and notifications.',
          'For safety: moderation, clean-content checks, reports, and audit logs.',
          'For support and legal responses.',
        ],
      },
      {
        heading: '3. Where it is stored',
        paragraphs: [
          'Account and content data live in a production database and cloud file storage with our hosting providers. Payment data lives with Stripe. Email delivery is handled by our email provider.',
        ],
      },
      {
        heading: '4. Local storage and cookies',
        paragraphs: [
          'We use a small amount of browser storage to keep you signed in and remember your theme preference. We do not use advertising trackers.',
        ],
      },
      {
        heading: '5. Sharing',
        paragraphs: [
          'We share data only with the service providers we need to run the platform (hosting, database, file storage, email, payments), and when required by law. We never sell your personal information.',
        ],
      },
      {
        heading: '6. Your rights',
        bullets: [
          'You can request access to, correction of, or deletion of your personal data.',
          'To make a request, email the contact address below.',
        ],
      },
      {
        heading: '7. Children',
        paragraphs: [
          'IAMMUZIK is not directed to children under 13, and we do not knowingly collect data from them. If you believe a child under 13 has an account, contact us and we will remove it.',
        ],
      },
      {
        heading: '8. Contact',
        paragraphs: [`Privacy questions? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },

  community: {
    slug: 'community',
    title: 'Community Guidelines',
    updated: LAST_UPDATED,
    intro:
      'These rules keep IAMMUZIK safe for real artists. They apply to all uploads, posts, battles, and messages.',
    sections: [
      {
        heading: 'What is not allowed',
        bullets: [
          'Stolen songs, uncleared samples, copyrighted beats you do not have permission to use, or another artist’s vocals.',
          'Threats, hate speech, harassment, bullying, or doxxing.',
          'Scams, fraud, or misleading offers.',
          'Sexual exploitation material, including involving minors.',
          'Malware, phishing, or anything that harms the platform or other users.',
          'Impersonation of another person or artist.',
        ],
      },
      {
        heading: 'Label your content',
        paragraphs: [
          'You must correctly mark explicit content as Explicit. Mislabeling can lead to removal or account action.',
        ],
      },
      {
        heading: 'Battles and uploads',
        bullets: [
          'Battle entries must be your original work or properly licensed material.',
          'Entering a battle gives IAMMUZIK permission to host and display that track within the battle.',
          'Public uploads can be moderated before they appear on The Wave.',
        ],
      },
      {
        heading: 'Enforcement',
        paragraphs: [
          'IAMMUZIK can remove or restrict content, cancel a battle entry, or suspend or close accounts that break these rules. Serious violations may be referred to law enforcement.',
        ],
      },
      {
        heading: 'Report it',
        paragraphs: [
          `See something that breaks these rules? Report it in the app or email ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  copyright: {
    slug: 'copyright',
    title: 'Copyright & DMCA Policy',
    updated: LAST_UPDATED,
    intro:
      'IAMMUZIK respects copyright. This page explains how to report infringing content and how counter-notices work.',
    sections: [
      {
        heading: 'Reporting infringement',
        paragraphs: [
          'If you believe content on IAMMUZIK infringes your copyright, send a takedown notice to our designated agent (below). Under 17 U.S.C. §512(c), a valid notice must include:',
        ],
        bullets: [
          'A physical or electronic signature of the copyright owner or their agent.',
          'Identification of the copyrighted work you claim is infringed.',
          'Identification of the specific IAMMUZIK material you want removed (with the URL).',
          'Your contact information (name, address, phone, email).',
          'A statement that you have a good-faith belief the use is not authorized.',
          'A statement, under penalty of perjury, that the information is accurate and that you are the owner or authorized to act.',
        ],
      },
      {
        heading: 'Designated DMCA agent',
        paragraphs: [
          `DMCA notices should be sent to: ${COPYRIGHT_EMAIL}.`,
          'Note: IAMMUZIK registers its designated agent with the U.S. Copyright Office and renews that registration as required (currently every three years, with a registration fee). The agent contact above is the address to use until the official directory listing is updated.',
        ],
      },
      {
        heading: 'Counter-notice',
        paragraphs: [
          'If your content was removed and you believe it was removed in error, you may send a counter-notice. If we receive a valid counter-notice, the material may be restored in 10–14 business days unless the original reporter files court action.',
        ],
      },
      {
        heading: 'Repeat infringers',
        paragraphs: [
          'IAMMUZIK terminates the accounts of users who are determined to be repeat infringers, in appropriate circumstances.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [`Copyright questions? Email ${COPYRIGHT_EMAIL}.`],
      },
    ],
  },

  uploads: {
    slug: 'uploads',
    title: 'Content & Upload Rules',
    updated: LAST_UPDATED,
    intro:
      'These rules apply to every audio file, lyric, artwork, sample, and vocal you upload to IAMMUZIK.',
    sections: [
      {
        heading: 'You must own it or have permission',
        bullets: [
          'You must own the music or hold all needed rights and permissions for anything you upload.',
          'This covers audio, artwork, samples, vocals, beats, and any other material.',
          'You may not upload stolen songs, uncleared samples, copyrighted beats you do not have permission to use, or another artist’s vocals.',
        ],
      },
      {
        heading: 'Ownership stays with you',
        paragraphs: [
          'Artists keep ownership of their original work unless a clearly disclosed contract says otherwise. Uploading to IAMMUZIK does not transfer your copyright to us.',
        ],
      },
      {
        heading: 'Battles and public content',
        bullets: [
          'A battle entry requires your permission for IAMMUZIK to host and display that track as part of the battle.',
          'Public uploads can be moderated before they appear on The Wave.',
          'Explicit content must be labeled correctly.',
        ],
      },
      {
        heading: 'File rules',
        bullets: [
          'Uploads are scanned and validated; files that fail validation are rejected.',
          'Storage and upload limits depend on your Contract tier.',
          'IAMMUZIK may remove material that violates these rules.',
        ],
      },
      {
        heading: 'Reporting',
        paragraphs: [
          `Report infringing or rule-breaking content in the app or email ${COPYRIGHT_EMAIL}.`,
        ],
      },
    ],
  },

  ai: {
    slug: 'ai',
    title: 'AI Policy',
    updated: LAST_UPDATED,
    intro:
      'IAMMUZIK uses AI for production help only. The most important rule: AI never writes your lyrics.',
    sections: [
      {
        heading: 'What AI never does',
        paragraphs: [
          'IAMMUZIK will not generate, suggest, rewrite, complete, or alter lyrics. This includes verses, hooks, choruses, rhymes, or any songwriting text. The lyrics and the songwriting are always 100% you.',
        ],
        bullets: [
          'No AI lyric generation.',
          'No AI lyric completion or rewriting.',
          'No AI-suggested verses, hooks, or choruses.',
        ],
      },
      {
        heading: 'What AI is allowed to do',
        bullets: [
          'Build instrumentals and beats.',
          'Perform your own written lyrics with a synthetic voice, only if you choose and only with voices you have the right to use.',
          'Create music videos and short promo trailers.',
          'Provide technical and audio assistance such as mastering-style processing.',
        ],
      },
      {
        heading: 'Enforced in code',
        paragraphs: [
          'This policy is enforced in the platform: requests shaped like lyric generation are blocked. The songwriting credit always stays with the creator.',
        ],
      },
      {
        heading: 'Disclosure',
        paragraphs: [
          'Where AI is used to produce audio, IAMMUZIK encourages creators to disclose AI-generated portions. Note: under current U.S. Copyright Office guidance, purely AI-generated audio may not be copyrightable; only substantial human creative contribution (such as your lyrics and arrangement) may be registrable.',
        ],
      },
      {
        heading: 'Voices',
        paragraphs: [
          'You may only use your own voice or a voice you have written, dated, scope-specific permission to use. Never clone a real artist without consent.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [`AI policy questions? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },

  contact: {
    slug: 'contact',
    title: 'Contact & Support',
    updated: LAST_UPDATED,
    intro: 'Need help? Here is how to reach the right team.',
    sections: [
      {
        heading: 'General support',
        paragraphs: [
          `Email ${CONTACT_EMAIL} for account help, billing questions, and general issues. We aim to respond within 2 business days.`,
        ],
      },
      {
        heading: 'Copyright & DMCA',
        paragraphs: [`Send takedown notices and copyright complaints to ${COPYRIGHT_EMAIL}. See the Copyright & DMCA Policy for what to include.`],
      },
      {
        heading: 'Privacy',
        paragraphs: [`For data access, correction, or deletion requests, email ${CONTACT_EMAIL}.`],
      },
      {
        heading: 'Safety emergencies',
        paragraphs: [
          'If someone is in immediate danger, contact your local emergency services first, then report it to us.',
        ],
      },
    ],
  },

  refunds: {
    slug: 'refunds',
    title: 'Refund & Cancellation Policy',
    updated: LAST_UPDATED,
    intro:
      'This policy applies to IAMMUZIK paid Contracts (subscriptions) and battle entry fees. It is required reading before you are charged.',
    sections: [
      {
        heading: 'Recurring billing',
        bullets: [
          'Subscriptions renew automatically at the price and interval shown on the Contracts page.',
          'You authorize IAMMUZIK (via Stripe) to charge your card until you cancel.',
          'You can cancel at any time from the Stripe Customer Portal or by contacting support.',
        ],
      },
      {
        heading: 'Cancelling',
        paragraphs: [
          'Cancellation stops future charges. You keep access until the end of the current paid period, after which your account returns to the Free tier.',
        ],
      },
      {
        heading: 'Refunds',
        bullets: [
          'We do not issue partial refunds for unused time in a billing period, except where required by law.',
          'If you believe you were charged in error, contact support within 30 days and we will review it.',
          'Refunds, when issued, are returned to the original payment method.',
        ],
      },
      {
        heading: 'What happens to your content',
        paragraphs: [
          'Your songs stay in your library when you downgrade, but tier-based features (such as AI tools or higher upload limits) stop working until you upgrade again.',
        ],
      },
      {
        heading: 'Battle entry fees',
        paragraphs: [
          'Battle entry fees are non-refundable once the battle closes or your entry is accepted, except where required by law.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [`Billing questions? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },
};

export const FOOTER_LINKS: { slug: string; title: string }[] = [
  { slug: 'terms', title: 'Terms of Service' },
  { slug: 'privacy', title: 'Privacy Policy' },
  { slug: 'community', title: 'Community Guidelines' },
  { slug: 'copyright', title: 'Copyright / DMCA' },
  { slug: 'uploads', title: 'Content & Upload Rules' },
  { slug: 'ai', title: 'AI Policy' },
  { slug: 'refunds', title: 'Refund & Cancellation' },
  { slug: 'contact', title: 'Contact' },
];
